import React, { useEffect } from 'react';
import { Alert, Divider, Typography, message } from 'antd';
import ComponentCard from './ComponentCard';
import AddContentMenu from './AddContentMenu';
import { createComponent } from 'utils/metaFlowComponents';

const { Title } = Typography;

const ScreenEditorPanel = ({
  screen,
  screens,
  onUpdateScreen,
  onAddComponent,
  onUpdateComponent,
  onDeleteComponent,
  onMoveComponent,
  onChangeComponentType,
}) => {
  // Meta only allows one NavigationList alone on a screen — drop other components from state.
  useEffect(() => {
    if (!screen) return;
    const lists = (screen.components || []).filter((c) => c.type === 'navigation_list');
    if (lists.length === 0) return;
    const hasOthers = (screen.components || []).some((c) => c.type !== 'navigation_list');
    if (hasOthers || lists.length > 1) {
      onUpdateScreen(screen.id, { components: lists.slice(0, 1) });
    }
  }, [screen, onUpdateScreen]);

  if (!screen) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
        Select a screen to edit its content
      </div>
    );
  }

  const navigationLists = (screen.components || []).filter((c) => c.type === 'navigation_list');
  const navigationListCount = navigationLists.length;
  const isNavigationListScreen = navigationListCount > 0;
  // Any other dynamic field (radio/checkbox/dropdown from API) blocks NavigationList.
  const hasOtherDynamicContent = (screen.components || []).some(
    (c) => c.type !== 'navigation_list' && c.data_mode === 'dynamic'
  );
  // Hide Add content entirely when a navigation list is present (max 1).
  const hideAddContent = isNavigationListScreen;
  const disabledTypes = hasOtherDynamicContent ? ['navigation_list'] : [];

  const contentComponents = isNavigationListScreen
    ? navigationLists
    : (screen.components || []).filter((c) => c.type !== 'button');
  const buttonComponents = isNavigationListScreen
    ? []
    : (screen.components || []).filter((c) => c.type === 'button');

  const handleAdd = (type) => {
    if (type === 'navigation_list') {
      if (isNavigationListScreen) {
        message.warning('Only 1 navigation list is allowed per screen');
        return;
      }
      if (hasOtherDynamicContent) {
        message.warning(
          'Remove other dynamic fields first. A navigation list must be the only content on the screen.'
        );
        return;
      }
      const component = createComponent(type);
      const hadOthers = (screen.components || []).length > 0;
      // Navigation list must be alone — replace footer/text/static fields.
      onUpdateScreen(screen.id, { components: [component] });
      if (hadOthers) {
        message.info(
          'Other components were removed — a navigation list must be the only content on the screen'
        );
      }
      return;
    }

    if (isNavigationListScreen) {
      return;
    }

    const buttonCount = (screen.components || []).filter((c) => c.type === 'button').length;
    if (type === 'button' && buttonCount >= 3) {
      return;
    }

    const component = createComponent(type);
    if (type === 'button') {
      onAddComponent(screen.id, component);
      return;
    }
    const withoutButtons = (screen.components || []).filter((c) => c.type !== 'button');
    const buttons = (screen.components || []).filter((c) => c.type === 'button');
    onUpdateScreen(screen.id, {
      components: [...withoutButtons, component, ...buttons],
    });
  };

  const screenIndex = screens.findIndex((s) => s.id === screen.id);
  const nextScreen = screens[screenIndex + 1];

  const getMoveFlags = (componentIndex) => {
    const components = screen.components || [];
    const isButton = components[componentIndex]?.type === 'button';
    const groupIndices = components
      .map((c, i) => ((c.type === 'button') === isButton ? i : -1))
      .filter((i) => i >= 0);
    const pos = groupIndices.indexOf(componentIndex);
    return {
      canMoveUp: pos > 0,
      canMoveDown: pos >= 0 && pos < groupIndices.length - 1,
    };
  };

  const renderComponentCard = (component) => {
    const index = screen.components.findIndex((c) => c.id === component.id);
    const { canMoveUp, canMoveDown } = getMoveFlags(index);
    return (
      <ComponentCard
        key={component.id}
        component={component}
        index={index}
        lockType={isNavigationListScreen}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onChange={(idx, updated) => onUpdateComponent(screen.id, idx, updated)}
        onDelete={(idx) => onDeleteComponent(screen.id, idx)}
        onMoveUp={(idx) => onMoveComponent?.(screen.id, idx, -1)}
        onMoveDown={(idx) => onMoveComponent?.(screen.id, idx, 1)}
        onTypeChange={(idx, type) => onChangeComponentType(screen.id, idx, type)}
      />
    );
  };

  return (
    <div>
      <Title level={5} style={{ marginTop: 0 }}>
        Edit content
      </Title>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
        Screen: {screen.title}
        {nextScreen ? ` → ${nextScreen.title}` : ' (last screen)'}
      </Typography.Text>

      {isNavigationListScreen ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Navigation list screen"
          description="Only one navigation list is allowed on this screen. Delete it to add other components again. Item tap advances the flow — no footer."
        />
      ) : null}

      {contentComponents.map((component) => renderComponentCard(component))}

      <AddContentMenu
        onAdd={handleAdd}
        disabled={!screen}
        hidden={hideAddContent}
        disabledTypes={disabledTypes}
      />

      {buttonComponents.length > 0 ? (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            Footer button
          </Typography.Text>
          {buttonComponents.map((component) => renderComponentCard(component))}
        </>
      ) : null}
    </div>
  );
};

export default ScreenEditorPanel;
